import { UploadedFile, useDeleteFile, useUploadFile } from '../data'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useErrorSnackbar } from 'shared/hooks'
import { MAX_FILE_QUANTITY } from '../utils/constants'
import { Attachment } from '../types/utils'

export interface FilesListProps {
  files: UploadedFile[]
  uploadFile: (file: File) => Promise<void>
  isUploading: boolean
  deleteFiles: (fileIds: string[]) => Promise<void>
  isDeleting: boolean
  clearAllFiles: () => Promise<void>
}

export interface useFilesListProps {
  initialFiles?: Attachment[]
}

export const useFilesList = ({
  initialFiles = [],
}: useFilesListProps = {}): FilesListProps => {
  const [files, setFiles] = useState<UploadedFile[]>(initialFiles)
  const [uploadFileMutation, { loading: isUploading }] = useUploadFile()
  const [deleteFilesMutaion, { loading: isDeleting }] = useDeleteFile()
  const [showError] = useErrorSnackbar()
  const { t } = useTranslation()

  const uploadFile = async (file: File) => {
    if (files.length >= MAX_FILE_QUANTITY) {
      showError(
        t(`featuredImage.errors.tooManyFiles`, {
          maxFileQuantity: MAX_FILE_QUANTITY,
        })
      )
      return
    }
    try {
      const response = await uploadFileMutation({
        variables: {
          input: {
            file,
          },
        },
      })
      if (!response || !response.data) return
      const { file: uploadedFile } = response.data.uploadFile
      setFiles(files => [...files, uploadedFile])
    } catch (e) {
      showError()
    }
  }

  const deleteFiles = async (fileIds: string[]) => {
    try {
      await deleteFilesMutaion({
        variables: {
          input: {
            fileIds,
          },
        },
      })
      setFiles(files => files.filter(file => !fileIds.includes(file.id)))
    } catch (e) {
      showError()
    }
  }

  const clearAllFiles = async () => {
    if (!files.length) return
    try {
      await deleteFilesMutaion({
        variables: {
          input: {
            fileIds: files.map(file => file.id),
          },
        },
      })
      setFiles([])
    } catch (e) {
      showError()
    }
  }

  return {
    files,
    uploadFile,
    isUploading,
    deleteFiles,
    isDeleting,
    clearAllFiles,
  }
}
